import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { QuizLayout } from '../components/QuizLayout';
import { flushQueue } from '../lib/api';
import { useQuizStore } from '../store/quizStore';
import { track, trackPurchase } from '../lib/analytics';
import { PLANS, type PlanId } from '../data/plans';

type AuthState =
  | { kind: 'no-stripe' }            // arrived without a Stripe session_id (placeholder/manual flow)
  | { kind: 'authenticating' }       // calling /api/auth-from-checkout
  | { kind: 'success'; booking: boolean } // signed in; navigating to booking (add-on) or dashboard
  | { kind: 'failed'; error: string };

export function ThankYou() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const plan = params.get('plan');
  const leadEmail = useQuizStore((s) => s.leadEmail);

  const [auth, setAuth] = useState<AuthState>({ kind: 'no-stripe' });

  // Last chance to flush queued lead/checkout-intent retries.
  useEffect(() => {
    void flushQueue();
  }, []);

  // Trade the just-completed Stripe checkout for a session cookie.
  useEffect(() => {
    const stripeSid = window.__stripeSessionId;
    if (!stripeSid) return;
    setAuth({ kind: 'authenticating' });
    track('checkout_completed', { plan });
    // jobtest.org GTM doc-specified event. `value` comes from PLANS table;
    // `transactionId` is the Stripe checkout session id (stashed pre-React
    // by the bootstrap script in index.html before being stripped off the
    // URL). Falls back to 0 / empty string if the plan param is unknown.
    const planRow = PLANS.find((p) => p.id === (plan as PlanId));
    trackPurchase({
      value: planRow?.priceUsd ?? 0,
      transactionId: stripeSid,
    });

    void fetch('/api/auth-from-checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stripe_session_id: stripeSid }),
      credentials: 'same-origin',
    })
      .then(async (res) => {
        const body = (await res.json().catch(() => ({}))) as {
          ok?: boolean;
          hasAddOn?: boolean;
          error?: string;
        };
        if (!res.ok || !body.ok) {
          setAuth({ kind: 'failed', error: body.error ?? `HTTP ${res.status}` });
          return;
        }
        // Clear the stashed sid so a manual refresh doesn't try to reuse it.
        try {
          delete window.__stripeSessionId;
        } catch {/* noop */}
        // Add-on buyers go to /booking first to schedule their 1:1; the
        // booking page redirects to /dashboard after a successful booking.
        // Everyone else goes straight to the dashboard (the AI Interview
        // Coach offer lives there now — first mock interview free).
        const dest = body.hasAddOn ? '/booking' : '/dashboard';
        setAuth({ kind: 'success', booking: !!body.hasAddOn });
        window.setTimeout(() => navigate(dest, { replace: true }), 900);
      })
      .catch((err) => {
        setAuth({ kind: 'failed', error: err instanceof Error ? err.message : 'network error' });
      });
  }, [navigate, plan]);

  return (
    <QuizLayout showBack={false}>
      {auth.kind === 'authenticating' && <Authenticating plan={plan} />}
      {auth.kind === 'success' && <Success booking={auth.booking} />}
      {auth.kind === 'failed' && <FailedFallback error={auth.error} leadEmail={leadEmail} plan={plan} />}
      {auth.kind === 'no-stripe' && <NoStripeFallback plan={plan} leadEmail={leadEmail} />}
    </QuizLayout>
  );
}

// ---------- Auto-auth states ----------

function Authenticating({ plan }: { plan: string | null }) {
  return (
    <div className="text-center">
      <p className="eyebrow mb-3">Payment received</p>
      <h1 className="text-display-md text-ink mb-4">
        Setting up your dashboard…
      </h1>
      <p className="text-ink-soft text-base mb-9 max-w-md mx-auto">
        Hang tight — we're activating your{' '}
        {plan ? <span className="font-semibold text-ink">{plan}</span> : 'subscription'}{' '}
        and unlocking your personalized career report.
      </p>
      <div className="flex justify-center mb-8">
        <span className="inline-block w-12 h-12 border-4 border-ink/15 border-t-brand rounded-full animate-spin" />
      </div>
    </div>
  );
}

function Success({ booking }: { booking: boolean }) {
  return (
    <div className="text-center">
      <p className="eyebrow mb-3 text-teal-700">You're in</p>
      <div className="flex justify-center mb-5">
        <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-teal-100">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#0d9d7c" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </span>
      </div>
      <h1 className="text-display-md text-ink mb-3">Welcome aboard.</h1>
      <p className="text-ink-soft text-base">
        {booking ? "Let's get your 1:1 session booked…" : 'Taking you to your dashboard…'}
      </p>
    </div>
  );
}

// ---------- Fallback (Stripe sign-in failed; let user proceed via magic link) ----------

function FailedFallback({
  error,
  leadEmail,
  plan,
}: {
  error: string;
  leadEmail: string | null;
  plan: string | null;
}) {
  return (
    <div>
      <p className="eyebrow mb-3">Payment received</p>
      <h1 className="text-display-md text-ink mb-3">Welcome aboard.</h1>
      <p className="text-ink-soft text-base mb-7">
        {plan ? `Your ${plan} plan is active. ` : 'Your subscription is active. '}
        We hit a snag signing you in automatically — no worries, your account is
        ready and we can email you a one-click link instead.
      </p>
      <div className="rounded-cta border-2 border-err/30 bg-err/5 px-4 py-3 mb-6 text-sm text-ink-soft">
        Auto sign-in error: <span className="font-mono text-xs">{error}</span>
      </div>
      <Link to="/login" className="btn-primary">
        {leadEmail ? `Email me a sign-in link →` : 'Sign in to my dashboard →'}
      </Link>
    </div>
  );
}

// ---------- No-Stripe fallback (placeholder flow, dev preview, etc.) ----------

function NoStripeFallback({ plan, leadEmail }: { plan: string | null; leadEmail: string | null }) {
  return (
    <div>
      <p className="eyebrow mb-3">You're in</p>
      <h1 className="text-display-md text-ink mb-4">Welcome aboard.</h1>
      <p className="text-ink-soft text-base mb-7">
        {plan
          ? `You picked the ${plan} plan. Your personalized career report is ready in your dashboard.`
          : `Your personalized career report is ready in your dashboard.`}
      </p>

      <ol className="space-y-3 mb-8">
        <li className="flex items-center gap-4 rounded-cta border-2 border-ink/10 bg-white px-5 py-4">
          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-teal-100 text-teal-700 text-xs font-bold flex items-center justify-center">
            1
          </span>
          <span className="flex-1 font-semibold text-ink">
            Click below to sign in. We'll email you a one-click link.
          </span>
        </li>
        <li className="flex items-center gap-4 rounded-cta border-2 border-ink/10 bg-white px-5 py-4">
          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-teal-100 text-teal-700 text-xs font-bold flex items-center justify-center">
            2
          </span>
          <span className="flex-1 font-semibold text-ink">
            Explore your personality, careers, resources, and live coaching.
          </span>
        </li>
      </ol>

      <Link to="/login" className="btn-primary">
        {leadEmail ? `Sign in as ${leadEmail} →` : 'Sign in to my dashboard →'}
      </Link>
    </div>
  );
}
